<!-- @format -->

<ng-container *transloco="let t">
  <fudis-dialog data-cy="plan-structure-edit-modal">
    <fudis-heading [level]="2">{{ this.modalValues.module.name | localizedString }}</fudis-heading>
    <fudis-dialog-content>
      @if (hasShownErrors$()) {
        <div class="sis-mb-sm">
          <sis-plan-structure-rule-error-summary
            #errorSummary
            [tabIndex]="-1"
            [ruleErrorStates]="ruleErrorStateService.shownErrorStates$()"
            (errorClick)="focusRule($event)">
          </sis-plan-structure-rule-error-summary>
        </div>
      }
      @if (data$ | async; as data) {
        @if (isModuleContentApplicationRequired(data) && showApplyForModuleContentWorkflowRequiredDialog(data)) {
          <fudis-notification [variant]="'danger'" [class]="'fudis-mb-sm'">
            <fudis-body-text>
              {{ t("SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.REQUIRED.APPROVAL_REQUIRED") }}
              <a
                fudisLink
                [title]="t('SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.REQUIRED.APPROVAL_REQUIRED_LINK_TEXT')"
                [size]="'md'"
                [href]="'#'"
                (click)="openApplyForModuleContentWorkflowDialog(data)"></a>
            </fudis-body-text>
          </fudis-notification>
        } @else if (
          isModuleContentApplicationRequired(data) &&
          !moduleContentApplicationRequested() &&
          !moduleContentApplicationAccepted() &&
          !moduleContentApplicationConditionallyAccepted() &&
          !hasSelectedModuleValidState()
        ) {
          <fudis-notification [variant]="'danger'" [class]="'fudis-mb-sm'">
            <fudis-body-text>
              {{ t("SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.REQUIRED.INVALID_CONTENTS") }}
            </fudis-body-text>
          </fudis-notification>
        } @else if (moduleContentApplicationChanged()) {
          <fudis-notification [variant]="'danger'" [class]="'fudis-mb-sm'">
            <fudis-body-text>
              {{ t("MODULE_CONTENT_APPROVAL_INFO_MODAL.CONTENT_NOT_AS_REQUESTED") }}
              <a
                fudisLink
                [title]="t('SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.SHOW_APPLICATION')"
                [size]="'md'"
                [href]="'#'"
                (click)="openModuleContentWorkflowDialog()"></a>
            </fudis-body-text>
          </fudis-notification>
        } @else if (moduleContentApplicationAccepted()) {
          <fudis-notification [variant]="'info'" [class]="'fudis-mb-sm'">
            <fudis-body-text>
              {{ t("SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.ACCEPTED_APPLICATION") }}
              <a
                fudisLink
                [title]="t('SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.SHOW_APPLICATION')"
                [size]="'md'"
                [href]="'#'"
                (click)="openModuleContentWorkflowDialog()"></a>
            </fudis-body-text>
          </fudis-notification>
        } @else if (moduleContentApplicationConditionallyAccepted()) {
          <fudis-notification [variant]="'info'" [class]="'fudis-mb-sm'">
            <fudis-body-text>
              {{ t("SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.CONDITIONALLY_ACCEPTED_APPLICATION") }}
              <a
                fudisLink
                [title]="t('SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.SHOW_APPLICATION')"
                [size]="'md'"
                [href]="'#'"
                (click)="openModuleContentWorkflowDialog()"></a>
            </fudis-body-text>
          </fudis-notification>
        } @else if (moduleContentApplicationRequested()) {
          <fudis-notification [variant]="'warning'" [class]="'fudis-mb-sm'">
            <fudis-body-text>
              {{ t("SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.APPROVAL_REQUESTED") }}
              <a
                fudisLink
                [title]="t('SIS_COMPONENTS.PLAN.MODULE_CONTENT_APPROVAL.SHOW_APPLICATION')"
                [size]="'md'"
                [href]="'#'"
                (click)="openModuleContentWorkflowDialog()"></a>
            </fudis-body-text>
          </fudis-notification>
        } @else if (hasInvalidSelections()) {
          <fudis-notification [variant]="'danger'" class="fudis-mb-sm">
            <fudis-body-text>
              {{ t("PLAN_STRUCTURE_EDIT.INVALID_SELECTIONS_IN_PLAN_GUIDANCE_TEXT") }}
            </fudis-body-text>
            <ul>
              <li class="fudis-mt-xxs">
                <a
                  fudisLink
                  [title]="t('PLAN_STRUCTURE_EDIT.INVALID_SELECTIONS_TITLE_LINK_TEXT')"
                  [size]="'md'"
                  [href]="'#'"
                  (click)="handleFocusClick($event)"></a>
              </li>
            </ul>
          </fudis-notification>
        }
        <fudis-grid [classes]="'modal-body'" [columnGap]="'none'" [columns]="{ sm: 'auto min-content' }">
          @if (modalValues?.module?.type !== "Education") {
            <fudis-icon-button
              #freeEditMenuButton
              [ariaLabel]="t('ARIA_LABEL.ADDITIONAL_MENU')"
              [size]="'small'"
              [variant]="'secondary'"
              [icon]="'three-dots'"
              [asMenuButton]="true">
              <fudis-dropdown-menu [align]="'left'">
                @for (option of actionOptions; track option) {
                  <fudis-dropdown-menu-item [label]="t(option.label)" (handleClick)="option.action()"> </fudis-dropdown-menu-item>
                }
              </fudis-dropdown-menu>
            </fudis-icon-button>
          }
          <sis-plan-structure-select-rule
            [parentModule]="data.selectedModule"
            [headingLevel]="3"
            [rule]="data.selectedModule.rule"
            [planData]="data.planData"
            [planRuleData]="data.planRuleData"
            [planStateObject]="data.planStateObject"
            [planValidationResult]="data.planValidationResult"
            [validatablePlan]="data.validatablePlan"
            [selectionUIState]="'ACTIVE'"
            [groupPrefix]="''"
            [ruleDepthLevel]="1">
          </sis-plan-structure-select-rule>
          <app-plan-structure-edit-invalid-selections
            [parentModule]="data.selectedModule"
            [planData]="data.planData"
            [planStateObject]="data.planStateObject"
            [planValidationResult]="data.planValidationResult"
            [validatablePlan]="data.validatablePlan"
            [allStudentStudyRights]="modalValues.allStudentStudyRights"
            [matchingStudyRight]="getStudyRightForModuleContentWorkflowApplication()"
            [headingLevel]="4"
            (workflowCreated)="workflowCreated($event)">
          </app-plan-structure-edit-invalid-selections>
        </fudis-grid>
      }
      @if (showLinkToStudyPath()) {
        <fudis-heading fudisDialogTitle [level]="4" [variant]="'lg'" [class]="getLinkToStudyPathClass()">
          {{ t("SELECTION_ASSISTANT.CHECK_STUDY_PATH_TITLE") }}
        </fudis-heading>
        <a
          fudisLink
          [href]="
            '/student/profile/study-rights?selectedStudyRightId=' +
            this.modalValues.validatablePlanStudyRight?.id +
            '#study-path-title-' +
            this.modalValues.validatablePlanStudyRight?.id
          "
          [title]="t('PLAN.SHOW_SELECTION_PATHS')"
          [external]="true"></a>
      }
    </fudis-dialog-content>
    <fudis-dialog-actions>
      <fudis-button fudisDialogClose [label]="t('SIS_COMPONENTS.BUTTON.CANCEL')" (handleClick)="dismiss()" [variant]="'secondary'" />
      <fudis-button
        data-cy="plan-structure-edit-modal-save-button"
        [label]="t('SIS_COMPONENTS.BUTTON.SAVE')"
        (handleClick)="submitClick$.next()" />
    </fudis-dialog-actions>
  </fudis-dialog>
</ng-container>
